// ---------------------------------------------------------------------------
// Tariff code sets and rule-parsing helpers, shared by the tariff components
// ---------------------------------------------------------------------------
// Phase 4 of collapsing bite-2.0's five private copies of the duty engine onto
// the one in biteservices. The duty MATH now runs server-side; what is left
// client-side is presentation and rule-shape parsing, and these are the pieces
// every tariff component needs.
//
// They were declared five times over. This is one copy, lifted verbatim from
// pages/tariffs/TariffPage.tsx — the Tariff Calculator, which is the
// behavioural reference where the copies disagree.
//
// Nothing here computes duty. The code sets drive badge and banner rendering;
// the parsers read the shape of a tariff rule — its applies-to list, its
// rejected-by list, its origin list, its effective window — so the UI can
// decide what to show. Duty math belongs to the engine.


// ---- Debug gate (OFF by default) ----
export const digitsOnly = (s = '') => String(s).replace(/\D/g, '');

export const COLUMN2_ISO2 = new Set(['BY', 'CU', 'KP', 'RU']);

// USMCA derivative steel (HTSUS Note 16(j)): 99038220 carries the 25% duty
// on non-U.S. content (and any U.S. content exceeding 40% of value);
// 99038221 is FREE for the U.S. content up to a 40%-of-value cap. We split
// the product value into two lines and clamp the FREE line at 40%.
export const USMCA_DERIV_STEEL_SPLIT_CODES = new Set(['99038221']);

// ---- IEEPA EU helpers (per-line) -------------------------------------------
export const IEEPA_OVER_15_CODES = new Set([
  '99030219',
  '99030272',
  '99039442',
  '99039440',
  '99039450',
  '99039452',
  '99039460',
  '99039462',
  '99030279',
  '99030287',
  '99030282',
  '99039464',
  '99039454',
  '99039444',
  '99038211',
  '99039466',
  '99039468',
  '99038226',
]);

export const IEEPA_TOP_UP_CODES = new Set([
  '99030220',
  '99030273',
  '99039443',
  '99039441',
  '99039451',
  '99039453',
  '99039461',
  '99039463',
  '99030280',
  '99030288',
  '99030283',
  '99039465',
  '99039455',
  '99039445',
  '99038210',
  '99039467',
  '99039469',
  '99038225',
]);

export const IEEPA_OVER_10_CODES = new Set([
  '99038208',
  '99038224',
  '99030538',
  '99030575',
]);

export const IEEPA_TOP_UP_10_CODES = new Set([
  '99038207',
  '99038223',
  '99030539',
  '99030576',
]);

export const IEEPA_OVER_12_5_CODES = new Set([
  '99030548',
  '99030570',
  '99030573',
]);

export const IEEPA_TOP_UP_12_5_CODES = new Set([
  '99030549',
  '99030571',
  '99030574',
]);

// Is "Free"?
export const isFreeBase = (v: unknown) =>
  String(v ?? '')
    .trim()
    .toLowerCase() === 'free';

export const normalizeFieldKey = (val: any): string =>
  String(val || '')
    .replace(/_/g, '') // so ALUMINUM_PERCENTAGE -> ALUMINUMPERCENTAGE
    .toUpperCase();

export const formatHtsPretty = (raw = '') => {
  const d = digitsOnly(String(raw));
  const parts = [d.slice(0, 4)];
  if (d.length > 4) {
    parts.push(d.slice(4, 6));
  }
  if (d.length > 6) {
    parts.push(d.slice(6, 8));
  }
  if (d.length > 8) {
    parts.push(d.slice(8, 10));
  }
  return parts.filter(Boolean).join('.');
};

export const parseMaybeDateMs = (v: any): number | null => {
  if (!v) return null;

  const dateObj = typeof v === 'string' ? new Date(v) : v;

  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
    const d1 = new Date(v);
    const t1 = d1.getTime();
    return Number.isNaN(t1) ? null : t1;
  }

  // Grab the year, month, and day in the local timezone
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');

  // Return UTC midnight for THAT specific calendar day to standardize it
  return new Date(`${y}-${m}-${d}T00:00:00Z`).getTime();
};

export const isWithinRange = (dateToCheck: any, from: any, to: any): boolean => {
  const t = parseMaybeDateMs(dateToCheck);
  if (t === null) {
    return true;
  }
  const start = parseMaybeDateMs(from) ?? -Infinity;
  const end = parseMaybeDateMs(to) ?? Infinity;
  return t >= start && t <= end;
};

export const parseAppliesToList = (val: any): any[] => {
  if (Array.isArray(val)) {
    // Old schema: array of objects or strings
    return val;
  }

  if (typeof val === 'string') {
    // New schema: "7317003000, 7317005503, ..."
    return val
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return [];
};

export const parseRejectedByList = (val: any): any[] => {
  if (Array.isArray(val)) {
    // Old schema: array of objects or strings
    return val;
  }

  if (typeof val === 'string') {
    // New schema: "99039401, 99039450, ..."
    return val
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  return [];
};

export const parseOriginCountryList = (val: any): any[] => {
  if (Array.isArray(val)) {
    // back-compat: old schema (array of objects or strings)
    return val;
  }

  if (typeof val === 'string') {
    return val
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean); // ["AD", "AE", ...]
  }

  return [];
};

export const RUSSIA_REPLACEMENT_CODES = new Set(['99039008', '99039009']);

// Section 232 Pharmaceuticals (9903.04.60 / 9903.04.62): these Ch.99 codes
// carry the full duty themselves (100% / 15%) and REPLACE the underlying
// article's base duty, so any line carrying one has its Base Duty forced to
// Free — same shape as the Russian Column-2 replacement codes.
export const PHARMA_232_BASE_FREE_CODES = new Set(['99030460', '99030462']);

export const normalizeCountry = (raw: string | null | undefined): string => {
  if (!raw) {
    return '';
  }

  let name = raw.trim().toLowerCase();

  // Handle Global wildcard explicitly
  if (name === 'global') {
    return 'global';
  }

  // Remove accents
  name = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Remove non-letters (spaces, commas, periods, etc.)
  const compact = name.replace(/[^a-z]/g, '');

  // Alias map using *normalized* keys (after stripping punctuation etc.)
  const aliases: Record<string, string> = {
    // Vietnam variants
    vietnam: 'vietnam',
    vietnamese: 'vietnam',
    vietnamvn: 'vietnam',
    vietname: 'vietnam',
    vietnamm: 'vietnam',

    // United States variants
    unitedstates: 'unitedstates',
    unitedstatesofamerica: 'unitedstates',
    usa: 'unitedstates',
    us: 'unitedstates',
    'u.s.a': 'unitedstates',
    'u.s': 'unitedstates',
    america: 'unitedstates',

    // United Kingdom variants
    unitedkingdom: 'unitedkingdom',
    greatbritain: 'unitedkingdom',
    uk: 'unitedkingdom',
    'u.k': 'unitedkingdom',
    england: 'unitedkingdom',

    // Korea variants
    korearepublicof: 'southkorea',
    southkorea: 'southkorea',
    republicofkorea: 'southkorea',
    koreademocraticpeoplesrepublicof: 'northkorea',
    northkorea: 'northkorea',
  };

  const alias = aliases[compact];
  if (alias) {
    return alias;
  }

  // Fallback: just use the compact normalized form
  return compact;
};
